import { useState } from 'react';
import { Alert, App, Button, Card, Form, Modal, Space, Typography } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { AdminLocation, CreateLocationBody } from '@goelectric/shared-types';
import { locationApi } from '../api/locationApi';
import { LocationForm } from '../components/LocationForm';
import { describeLocationApiError } from '../utils/locationErrors';
import type { LocationFormState } from '../utils/locationForm';

/**
 * Página de alta de Locación (`/ubicaciones/nueva`, solo `SUPER_ADMIN` — el guard de la
 * ruta lo garantiza). Compone el wizard `LocationForm` con el POST y el resultado: en
 * éxito muestra SIEMPRE el modal de confirmación; recién al cerrarlo navega al detalle de
 * la locación recién creada. En error muestra el `message` del backend y marca el campo
 * si el error lo identifica (ADR 0021).
 *
 * Mismo patrón que `CreateAllyPage`: el alta puede completarse en el CRM pero fallar la
 * sincronización con el core CitrineOS (`syncStatus` `PENDIENTE`/`ERROR`). El modal
 * advierte ese caso y ofrece reintentar desde el detalle.
 */
export function CreateLocationPage() {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const [form] = Form.useForm<LocationFormState>();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Campo que marcó el backend en el último error; el wizard salta a su paso si no es el actual.
  const [errorField, setErrorField] = useState<string | null>(null);
  // Confirmación del alta: siempre presente tras crear; trae los datos y el `syncStatus`.
  const [confirmation, setConfirmation] = useState<AdminLocation | null>(null);

  const goToList = () => navigate('/ubicaciones');

  /** Cierra el modal (o el X/ESC) y recién ahí navega al detalle de la locación creada. */
  const closeConfirmation = () => {
    const locationId = confirmation?.id;
    setConfirmation(null);
    if (locationId) {
      navigate(`/ubicaciones/${encodeURIComponent(locationId)}`);
    }
  };

  const handleSubmit = async (body: CreateLocationBody) => {
    setSubmitting(true);
    setError(null);
    setErrorField(null);
    try {
      const created = await locationApi.create(body);
      message.success('Ubicación creada.');
      setConfirmation(created);
    } catch (caught) {
      const { message: detail, field } = describeLocationApiError(caught);
      setError(detail);
      setErrorField(field ?? null);
      if (field) {
        form.setFields([{ name: field as keyof LocationFormState, errors: [detail] }]);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const zone = confirmation
    ? [confirmation.city, confirmation.state].filter(Boolean).join(' · ')
    : '';
  // El core pudo no confirmar la Location: el alta sirvió pero falta sincronizar.
  // Con o sin aliado, el backend toma el tenant de plataforma y la sincronización es
  // reintentable desde el detalle.
  const syncPending = confirmation !== null && confirmation.syncStatus !== 'SYNCED';

  return (
    <div className="locaciones-page">
      <div className="locaciones-page__header">
        <Space align="center">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            aria-label="Volver al listado de ubicaciones"
            onClick={goToList}
          />
          <Typography.Title level={3} style={{ margin: 0 }}>
            Nueva ubicación
          </Typography.Title>
        </Space>
      </div>

      {error && (
        <Alert
          type="error"
          showIcon
          role="alert"
          message="No se pudo crear la ubicación"
          description={error}
        />
      )}

      <Card>
        <LocationForm
          form={form}
          onSubmit={handleSubmit}
          submitting={submitting}
          errorField={errorField}
        />
      </Card>

      <Modal
        open={confirmation !== null}
        rootClassName="locaciones-created-modal"
        title={
          <Typography.Title
            level={3}
            className="locaciones-created-modal__headline"
            style={{ margin: 0 }}
          >
            Tenemos una nueva ubicación
          </Typography.Title>
        }
        onCancel={closeConfirmation}
        footer={
          <Button type="primary" onClick={closeConfirmation}>
            Ver detalles de la ubicación
          </Button>
        }
        width={640}
      >
        <Typography.Paragraph className="locaciones-created-modal__lead">
          Se creó la ubicación <Typography.Text strong>{confirmation?.name}</Typography.Text>
          {zone ? (
            <>
              {' '}
              en <Typography.Text>{zone}</Typography.Text>
            </>
          ) : null}
          .
        </Typography.Paragraph>

        {syncPending ? (
          <Typography.Paragraph
            className="locaciones-created-modal__lead"
            style={{ marginBottom: 0 }}
          >
            La ubicación quedó creada, pero{' '}
            <Typography.Text strong>todavía no está sincronizada con el core</Typography.Text>. Puedes
            reintentar la sincronización desde el detalle.
          </Typography.Paragraph>
        ) : (
          <Typography.Paragraph
            className="locaciones-created-modal__lead"
            style={{ marginBottom: 0 }}
          >
            La ubicación ya está sincronizada con el core.
          </Typography.Paragraph>
        )}
      </Modal>
    </div>
  );
}
